import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  User,
  Mail,
  Building2,
  Phone,
  MapPin,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import { createMember, listMembershipPlans, calculateMembershipFee, getUnifiedPlanDetails, getRenewalPrefill, DEFAULT_BASE_URL } from '../services/api';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { withLocalizedPlan } from '../utils/membershipPlanLocalization';

// InputField component defined outside to prevent recreation on each render
const InputField = ({
  label,
  name,
  type = 'text',
  required = false,
  icon: Icon,
  placeholder,
  maxLength,
  value,
  error,
  onChange,
  ...props
}) => {
  const fieldValue = String(value || '');
  const hasError = !!error;
  const errorText = error;

  const baseClass =
    'block w-full pr-3 py-3 border-2 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-primary-500 text-sm transition-all';
  const paddingLeft = Icon ? 'pl-10' : 'pl-4';
  const stateClass = hasError
    ? 'border-red-500 bg-red-50 focus:ring-red-300'
    : 'border-gray-300 bg-white focus:ring-primary-300';

  return (
    <div>
      <label
        htmlFor={name}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon
              className={`h-4 w-4 ${
                hasError ? 'text-red-500' : 'text-gray-400'
              }`}
            />
          </div>
        )}
        {type === 'textarea' ? (
          <textarea
            id={name}
            name={name}
            rows={props.rows || 3}
            maxLength={maxLength}
            className={`${baseClass} ${paddingLeft} ${stateClass}`}
            placeholder={placeholder}
            value={fieldValue}
            onChange={onChange}
            {...props}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={type}
            maxLength={maxLength}
            className={`${baseClass} ${paddingLeft} ${stateClass}`}
            placeholder={placeholder}
            value={fieldValue}
            onChange={onChange}
            {...props}
          />
        )}
      </div>
      {hasError && (
        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {errorText}
        </p>
      )}
    </div>
  );
};

InputField.displayName = 'InputField';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    mobile: '',
    email: '',
    applicantName: '',
    representativeName: '',
    gender: '',
    telephone: '',
    aadhar: '',
    pancard: '',
    address: '',
    state: '',
    district: '',
    city: '',
    pincode: '',
    website: '',
    gst: '',
    natureOfBusiness: '',
    industry: '',
    workerCount: '',
    membershipPlanId: '',
    // Internal fields (auto-populated, not shown in form)
    personName: '',
    organizationName: '',
    location: '',
    membershipType: '',
    startDate: '',
    endDate: '',
  });

  const [plans, setPlans] = useState([]);
  const [unifiedPlan, setUnifiedPlan] = useState(null);
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [calculatingFee, setCalculatingFee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const calculateEndDate = useCallback((duration, unit) => {
    if (!duration) return '';
    const date = new Date();
    const d = parseInt(duration, 10);
    if (unit === 'Months' || unit === 'months') {
      date.setMonth(date.getMonth() + d);
    } else if (unit === 'Years' || unit === 'years') {
      date.setFullYear(date.getFullYear() + d);
    }
    return date.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const data = await listMembershipPlans();
        const activePlans = data.filter(p => p.isActive).map(p => withLocalizedPlan(p, getCurrentLanguage()));
        setPlans(activePlans);

        // Find unified plan
        const unified = activePlans.find(p => p.type === 'unified');
        if (unified) {
          setUnifiedPlan(unified);
          setSelectedPlan(unified);
          setFormData(prev => ({
            ...prev,
            membershipPlanId: unified._id || unified.id,
            membershipType: unified.name || '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: calculateEndDate(unified.duration, unified.durationUnit),
          }));
        } else {
          // Fallback: try to get unified plan details directly
          try {
            const unifiedDetails = withLocalizedPlan(await getUnifiedPlanDetails(), getCurrentLanguage());
            setUnifiedPlan(unifiedDetails);
            setSelectedPlan(unifiedDetails);
            setFormData(prev => ({
              ...prev,
              membershipPlanId: unifiedDetails._id || unifiedDetails.id,
              membershipType: unifiedDetails.name || '',
              startDate: new Date().toISOString().split('T')[0],
              endDate: calculateEndDate(unifiedDetails.duration, unifiedDetails.durationUnit),
            }));
          } catch (err) {
            console.error('Failed to load unified plan:', err);
          }
        }

        // Handle renewal case - get member data if memberId provided
        const renewal = searchParams.get('renewal');
        const memberId = searchParams.get('memberId');
        if (renewal === 'true' && memberId) {
          const workerFromUrl = searchParams.get('workerCount');
          let workers = workerFromUrl ? parseInt(workerFromUrl, 10) : NaN;
          if (!Number.isFinite(workers) || workers < 0) {
            try {
              const prefill = await getRenewalPrefill(memberId);
              if (prefill && prefill.workerCount != null) {
                workers = Number(prefill.workerCount);
              }
            } catch (err) {
              console.error('Failed to load renewal prefill:', err);
            }
          }
          if (Number.isFinite(workers) && workers >= 0) {
            setFormData(prev => ({
              ...prev,
              workerCount: String(workers),
            }));
            try {
              const feeData = await calculateMembershipFee(workers);
              setFeeCalculation(feeData);
            } catch (feeErr) {
              console.error('Failed to calculate renewal fee:', feeErr);
            }
          }
        }

        const planId = searchParams.get('planId');
        if (planId && !unified) {
          const plan = activePlans.find(p => (p._id || p.id) === planId);
          if (plan) {
            setFormData(prev => ({
              ...prev,
              membershipPlanId: planId,
              membershipType: plan.name || '',
              startDate: new Date().toISOString().split('T')[0],
              endDate: calculateEndDate(plan.duration, plan.durationUnit),
            }));
            setSelectedPlan(plan);
          }
        }

        const status = searchParams.get('status');
        if (status === 'success') {
          setPaymentStatus('success');
          setTimeout(() => {
            setFormData({
              mobile: '',
              email: '',
              applicantName: '',
              representativeName: '',
              gender: '',
              telephone: '',
              aadhar: '',
              pancard: '',
              address: '',
              state: '',
              district: '',
              city: '',
              pincode: '',
              website: '',
              gst: '',
              natureOfBusiness: '',
              industry: '',
              workerCount: '',
              membershipPlanId: '',
              personName: '',
              organizationName: '',
              location: '',
              membershipType: '',
              startDate: '',
              endDate: '',
            });
            setFeeCalculation(null);
            setErrors({});
          }, 3000);
        }
        if (status === 'failed') setPaymentStatus('failed');
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load plans:', error);
        }
        setErrorMessage('Failed to load membership plans. Please refresh the page.');
      } finally {
        setIsLoadingPlans(false);
      }
    };

    loadPlans();
  }, [searchParams, calculateEndDate]);

  // validation: partial input allowed, errors only after user stops typing or on blur
  const validateField = useCallback((name, rawValue) => {
    const value = (rawValue || '').trim();
    
    // Don't validate empty non-required fields
    const requiredFields = [
      'mobile',
      'email',
      'applicantName',
      'organizationName',
      'representativeName',
      'gender',
      'aadhar',
      'pancard',
      'address',
      'state',
      'district',
      'city',
      'pincode',
      'website',
      'natureOfBusiness',
      'industry',
      'membershipPlanId',
    ];
    
    // For non-required fields that are empty, skip validation
    if (!value && !requiredFields.includes(name)) {
      return '';
    }

    // For required fields, only validate when user has stopped typing (on blur or submit)
    // Return empty string while typing
    if (requiredFields.includes(name)) {
      // While typing, don't show errors for required fields
      // Only validate format if value exists
      if (value) {
        switch (name) {
          case 'mobile':
            if (!/^[6-9]\d{0,9}$/.test(value) || value.length > 10) {
              return getText('registration.invalidMobile') || 'Please enter a valid 10-digit mobile number';
            }
            break;
          case 'email':
            if (value.includes('@')) {
              if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return getText('registration.invalidEmail') || 'Please enter a valid email address';
              }
            }
            break;
          case 'applicantName':
          case 'organizationName':
          case 'representativeName':
            if (value.length < 2) {
              return getText('registration.required') || 'This field is required';
            }
            break;
          case 'gender':
            if (!value) {
              return getText('registration.required') || 'This field is required';
            }
            break;
          case 'aadhar':
            if (value.length !== 12 || !/^\d{12}$/.test(value)) {
              return getText('registration.invalidAadhar') || 'Please enter a valid 12-digit Aadhar number';
            }
            break;
          case 'pancard':
            if (value.length !== 10 || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase())) {
              return getText('registration.invalidPAN') || 'Please enter a valid PAN card number';
            }
            break;
          case 'state':
          case 'district':
          case 'city':
            if (value.length < 2) {
              return getText('registration.required') || 'This field is required';
            }
            break;
          case 'pincode':
            if (value.length !== 6 || !/^\d{6}$/.test(value)) {
              return getText('registration.invalidPincode') || 'Please enter a valid 6-digit pincode';
            }
            break;
          case 'address':
            if (value.length < 10) {
              return getText('registration.required') || 'Address must be at least 10 characters';
            }
            break;
          case 'natureOfBusiness':
          case 'industry':
            if (value.length < 2) {
              return getText('registration.required') || 'This field is required';
            }
            break;
          case 'workerCount':
            if (!value) {
              return 'Please enter the number of workers';
            }
            const workerCountNum = parseInt(value, 10);
            if (isNaN(workerCountNum) || workerCountNum < 1) {
              return 'Worker count must be at least 1';
            }
            break;
          default:
            break;
        }
      }
      return '';
    }

    // For non-required fields or format validation only
    switch (name) {
      case 'pincode':
        if (value && value.length > 0) {
          if (!/^\d{0,6}$/.test(value)) {
            return getText('registration.invalidPincode') || 'Please enter numbers only';
          }
          if (value.length === 6 && !/^\d{6}$/.test(value)) {
            return getText('registration.invalidPincode') || 'Please enter a valid 6-digit pincode';
          }
        }
        break;
      case 'aadhar':
        if (value && value.length > 0) {
          if (!/^\d{0,12}$/.test(value)) {
            return getText('registration.invalidAadhar') || 'Please enter numbers only';
          }
          if (value.length === 12 && !/^\d{12}$/.test(value)) {
            return getText('registration.invalidAadhar') || 'Please enter a valid 12-digit Aadhar number';
          }
        }
        break;
      case 'pancard':
        if (value && value.length > 0) {
          if (!/^[A-Z0-9]{0,10}$/.test(value.toUpperCase())) {
            return getText('registration.invalidPAN') || 'Please enter valid PAN characters';
          }
          if (value.length === 10 && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase())) {
            return getText('registration.invalidPAN') || 'Please enter a valid PAN card number';
          }
        }
        break;
      case 'gst':
        if (value && value.length > 0) {
          if (!/^[0-9A-Z]{0,15}$/.test(value.toUpperCase())) {
            return 'Please enter valid GST characters';
          }
          if (value.length === 15 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase())) {
            return 'Please enter a valid GST number';
          }
        }
        break;
      case 'website':
        if (value && value.length > 0) {
          try {
            const url = value.startsWith('http') ? value : `https://${value}`;
            new URL(url);
          } catch {
            return 'Please enter a valid URL';
          }
        }
        break;
      default:
        break;
    }

    return '';
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'mobile',
      'email',
      'applicantName',
      'organizationName',
      'representativeName',
      'gender',
      'aadhar',
      'pancard',
      'address',
      'state',
      'district',
      'city',
      'pincode',
      'natureOfBusiness',
      'industry',
      'membershipPlanId',
    ];

    requiredFields.forEach(field => {
      if (field === 'gender') {
        // Special handling for gender (radio button)
        if (!formData[field]) {
          newErrors[field] = getText('registration.required') || 'This field is required';
        }
      } else if (!formData[field] || formData[field].trim().length === 0) {
        newErrors[field] = getText('registration.required') || 'This field is required';
      } else {
        const err = validateField(field, formData[field]);
        if (err) newErrors[field] = err;
      }
    });

    // Validate optional fields only if they have values (telephone, GST, and website)
    ['telephone', 'gst', 'website'].forEach(field => {
      const v = formData[field];
      if (v && v.trim()) {
        const err = validateField(field, v);
        if (err) newErrors[field] = err;
      }
    });

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Process value based on field type
    if (['mobile', 'aadhar', 'pincode', 'telephone'].includes(name)) {
      processedValue = value.replace(/\D/g, '');
    } else if (name === 'pancard' || name === 'gst') {
      processedValue = value.toUpperCase();
    }

    // Update form data only - no validation, no state changes that cause re-renders
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: processedValue,
      };

      // Update dependent fields
      if (name === 'applicantName') {
        updated.personName = processedValue;
      }
      if (name === 'address') {
        updated.location = processedValue;
      }
      // Note: natureOfBusiness and industry are separate fields - do NOT copy between them

      // Handle worker count change and fee calculation
      if (name === 'workerCount' && processedValue) {
        const workerCountNum = parseInt(processedValue, 10);
        if (!isNaN(workerCountNum) && workerCountNum > 0) {
          // Calculate fee asynchronously
          setCalculatingFee(true);
          calculateMembershipFee(workerCountNum)
            .then(feeData => {
              setFeeCalculation(feeData);
              setCalculatingFee(false);
            })
            .catch(err => {
              console.error('Failed to calculate fee:', err);
              setFeeCalculation(null);
              setCalculatingFee(false);
            });
        } else {
          setFeeCalculation(null);
        }
      }

      return updated;
    });
  }, [plans, calculateEndDate]);

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMessage('');

    // Validate form - errors will be set by validateForm
    const validation = validateForm();
    if (!validation.isValid) {
      setErrorMessage(
        getText('registration.error') || 'Please fix the errors below',
      );
      setTimeout(() => {
        const firstErrorField = Object.keys(validation.errors)[0];
        if (firstErrorField) {
          const element = document.getElementById(firstErrorField);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return;
    }

    setSubmitting(true);
    setPaymentStatus(null);

    try {
      // Get unified plan or selected plan
      const selectedPlanData = unifiedPlan || plans.find(
        p => (p._id || p.id) === formData.membershipPlanId,
      );
      if (!selectedPlanData) {
        throw new Error(
          'Membership plan not found. Please refresh and try again.',
        );
      }

      // Validate worker count
      const workerCountNum = parseInt(formData.workerCount, 10);
      if (!workerCountNum || workerCountNum < 1) {
        throw new Error('Please enter a valid worker count (at least 1)');
      }

      // Use calculated fee if available, otherwise use plan price
      const membershipFee = feeCalculation?.fee || selectedPlanData.price || 0;

      const payload = {
        personName: (formData.applicantName || formData.personName || '').trim(),
        organizationName: (formData.organizationName || '').trim(),
        email: (formData.email || '').trim(),
        mobile: (formData.mobile || '').trim(),
        location: (formData.address || formData.location || '').trim(),
        industry: (formData.industry || '').trim(), // Industry is separate from natureOfBusiness
        natureOfBusiness: (formData.natureOfBusiness || '').trim(), // Separate field
        workerCount: workerCountNum,
        membershipPlanId: formData.membershipPlanId || (selectedPlanData._id || selectedPlanData.id),
        membershipPlanName: selectedPlanData.name,
        representativeName: (formData.representativeName || '').trim(),
        gender: formData.gender || '',
        telephone: (formData.telephone || '').trim() || undefined,
        aadhar: (formData.aadhar || '').trim(),
        pancard: (formData.pancard || '').trim(),
        state: (formData.state || '').trim(),
        district: (formData.district || '').trim(),
        city: (formData.city || '').trim(),
        pincode: (formData.pincode || '').trim(),
        website: formData.website && formData.website.trim() ? formData.website.trim() : undefined,
        gst: (formData.gst || '').trim() || undefined,
        membershipType: (formData.membershipType || '').trim(),
        startDate: formData.startDate || '',
        endDate: formData.endDate || '',
        createdAt: new Date().toISOString(),
        status: 'pending',
      };

      const created = await createMember(payload);
      setSelectedPlan(selectedPlanData);

      const baseUrl = DEFAULT_BASE_URL.replace(/\/$/, '');
      const paymentUrl = `${baseUrl}/payments/create-order`;

      const memberId = created._id || created.id;
      const planId = selectedPlanData._id || selectedPlanData.id;
      const idempotencyKey = `membership:${memberId}:${planId}`;

      const res = await fetch(paymentUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // Include credentials for CORS
        body: JSON.stringify({
          memberId,
          amount: membershipFee,
          planName: selectedPlanData.name,
          membershipPlanId: planId,
          idempotencyKey,
          notes: {
            email: formData.email,
            mobile: formData.mobile
          }
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            'Failed to generate payment link. Please try again.',
        );
      }

      const data = await res.json();
      if (!data?.order?.id) {
        throw new Error('Payment order not generated. Please try again.');
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        throw new Error('Unable to load Razorpay checkout. Please check your network and try again.');
      }

      const razorpayKeyId = (data.keyId && String(data.keyId).trim()) || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKeyId) {
        throw new Error('Payment gateway is not configured. Please contact support.');
      }
      if (process.env.NODE_ENV === 'development' && /^rzp_(live|test)_xxxxxx$/.test(razorpayKeyId)) {
        console.warn(
          '[Razorpay] Key ID is still a placeholder (rzp_live_xxxxxx / rzp_test_xxxxxx). ' +
            'Set RAZORPAY_KEY_ID in backend .env or REACT_APP_RAZORPAY_KEY_ID for the client.'
        );
      }

      setPaymentStatus('redirecting');

      const razorpay = new window.Razorpay({
        key: razorpayKeyId,
        amount: data.order.amount,
        currency: data.order.currency || 'INR',
        name: 'Industrial Federation of Maharashtra Region',
        description: `Membership Payment - ${selectedPlanData.name}`,
        order_id: data.order.id,
        prefill: {
          name: payload.personName,
          email: payload.email,
          contact: payload.mobile
        },
        theme: {
          color: '#3399cc'
        },
        modal: {
          ondismiss: () => {
            setPaymentStatus('failed');
            setErrorMessage('Payment was cancelled. You can try again anytime.');
          }
        },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${baseUrl}/payments/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify(response)
            });

            const verifyData = await verifyRes.json().catch(() => ({}));
            if (!verifyRes.ok || !verifyData.success) {
              throw new Error(verifyData.message || 'Payment verification failed. Please contact support.');
            }

            setPaymentStatus('success');
            setErrorMessage('');
          } catch (verifyError) {
            setPaymentStatus('failed');
            setErrorMessage(verifyError.message || 'Payment verification failed. Please contact support.');
          }
        }
      });

      razorpay.on('payment.failed', (response) => {
        setPaymentStatus('failed');
        const reason =
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed. Please try again.';
        setErrorMessage(reason);
      });

      razorpay.open();
      return;
    } catch (err) {
      setPaymentStatus('failed');
      if (process.env.NODE_ENV === 'development') {
        console.error('Registration error:', err);
      }

      let errorMsg = 'Registration failed. Please try again.';
      if (err.message) {
        errorMsg = err.message;
      } else if (err.response && err.response.data) {
        errorMsg =
          err.response.data.message ||
          err.response.data.error ||
          errorMsg;
      } else if (err.data) {
        // Handle API service layer errors
        errorMsg = err.data.message || err.data.error || err.message || errorMsg;
      }

      // Check if error is related to email duplication
      const lowerErrorMsg = errorMsg.toLowerCase();
      if (lowerErrorMsg.includes('email') && (lowerErrorMsg.includes('already') || lowerErrorMsg.includes('exists') || lowerErrorMsg.includes('duplicate'))) {
        setErrors(prev => ({
          ...prev,
          email: 'This email address is already registered. Please use a different email or contact support.'
        }));
        // Scroll to email field
        setTimeout(() => {
          const emailField = document.getElementById('email');
          if (emailField) {
            emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            emailField.focus();
          }
        }, 100);
      }

      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="mb-6">
              <div className="mb-4">
                <Link
                  to="/membership"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg"
                >
                  <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                  <span className="font-medium text-sm">Back to Membership</span>
                </Link>
              </div>
            </div>

            <div className="text-center">
              <div className="flex justify-center items-center mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary-100 rounded-full">
                    <Building2 className="h-8 w-8 text-primary-600" />
                  </div>
                  <span className="text-3xl font-bold text-gray-900">IFM</span>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {getText('registration.title') || 'Member Registration'}
              </h1>
              <p className="text-lg text-gray-600">
                {getText('registration.subtitle') ||
                  'Please fill in all required fields to complete your membership registration'}
              </p>
            </div>
          </div>

          {paymentStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-green-800">
                    Registration Successful!
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your membership has been registered successfully. You will
                    receive a confirmation email shortly.
                  </p>
                  <button
                    onClick={() => navigate('/')}
                    className="mt-3 text-sm text-green-600 hover:text-green-700 font-semibold underline"
                  >
                    Go to Homepage →
                  </button>
                </div>
              </div>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800">
                    Payment Failed
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    There was an issue processing your payment. Please try
                    again.
                  </p>
                </div>
              </div>
            </div>
          )}

          {paymentStatus === 'redirecting' && (
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
              <div className="flex items-start">
                <Loader2 className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0 animate-spin" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-800">
                    Redirecting to Payment Gateway
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Please complete your payment to activate your membership. Do
                    not refresh the page.
                  </p>
                </div>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                  </div>
                </div>
                <button
                  onClick={() => setErrorMessage('')}
                  className="text-red-400 hover:text-red-600 ml-2"
                  aria-label="Dismiss error"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* Personal Details */}
              <div>
                <div className="mb-6 pb-4 border-b-2 border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <User className="h-5 w-5 text-primary-600" />
                    {getText('registration.personalDetails') ||
                      'Personal Details'}
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label={getText('registration.mobile') || 'WhatsApp / Mobile No.'}
                    name="mobile"
                    type="tel"
                    required
                    icon={Phone}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                    value={formData.mobile}
                    error={errors.mobile}
                    onChange={handleChange}
                  />

                  <InputField
                    label={getText('registration.email') || 'Email Address'}
                    name="email"
                    type="email"
                    required
                    icon={Mail}
                    placeholder={
                      getText('registration.emailPlaceholder') ||
                      'Enter email address'
                    }
                    value={formData.email}
                    error={errors.email}
                    onChange={handleChange}
                  />

                  <InputField
                    label={
                      getText('registration.applicantName') || 'Applicant Name (Shri./Smt./M/s.)'
                    }
                    name="applicantName"
                    type="text"
                    required
                    icon={User}
                    placeholder="Enter applicant name"
                    value={formData.applicantName}
                    error={errors.applicantName}
                    onChange={handleChange}
                  />

                  <InputField
                    label={
                      getText('registration.organizationName') ||
                      'Organization Name'
                    }
                    name="organizationName"
                    type="text"
                    required
                    icon={Building2}
                    placeholder="Enter organization name"
                    value={formData.organizationName}
                    error={errors.organizationName}
                    onChange={handleChange}
                  />

                  <InputField
                    label={
                      getText('registration.representativeName') ||
                      'Representative Name'
                    }
                    name="representativeName"
                    type="text"
                    required
                    placeholder="Enter representative name"
                    value={formData.representativeName}
                    error={errors.representativeName}
                    onChange={handleChange}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {getText('registration.gender') || 'Gender'} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-6 mt-2">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value="Male"
                          checked={formData.gender === 'Male'}
                          onChange={handleChange}
                          className={`w-4 h-4 text-primary-600 focus:ring-primary-200 ${errors.gender ? 'border-red-500' : ''}`}
                        />
                        <span className="text-sm text-gray-700">
                          {getText('registration.gender.male') || 'Male'}
                        </span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value="Female"
                          checked={formData.gender === 'Female'}
                          onChange={handleChange}
                          className={`w-4 h-4 text-primary-600 focus:ring-primary-200 ${errors.gender ? 'border-red-500' : ''}`}
                        />
                        <span className="text-sm text-gray-700">
                          {getText('registration.gender.female') || 'Female'}
                        </span>
                      </label>
                    </div>
                    {errors.gender && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.gender}
                      </p>
                    )}
                  </div>

                  <InputField
                    label={getText('registration.telephone') || 'Telephone'}
                    name="telephone"
                    type="tel"
                    placeholder="Enter telephone number (optional)"
                    maxLength={15}
                    value={formData.telephone}
                    error={errors.telephone}
                    onChange={handleChange}
                  />

                  <InputField
                    label={getText('registration.aadhar') || 'Aadhar No.'}
                    name="aadhar"
                    type="text"
                    required
                    placeholder="Enter 12-digit Aadhar number"
                    maxLength={12}
                    value={formData.aadhar}
                    error={errors.aadhar}
                    onChange={handleChange}
                  />

                  <InputField
                    label={getText('registration.pancard') || 'PAN Card No.'}
                    name="pancard"
                    type="text"
                    required
                    placeholder="Enter PAN card number"
                    maxLength={10}
                    value={formData.pancard}
                    error={errors.pancard}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <div className="mb-6 pb-4 border-b-2 border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary-600" />
                    Address Details
                  </h2>
                </div>

                <div className="space-y-6">
                  <InputField
                    label={getText('registration.address') || 'Address'}
                    name="address"
                    type="textarea"
                    required
                    icon={MapPin}
                    placeholder="Enter complete address"
                    rows={3}
                    value={formData.address}
                    error={errors.address}
                    onChange={handleChange}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      label={getText('registration.state') || 'State'}
                      name="state"
                      type="text"
                      required
                      placeholder="Enter state"
                      value={formData.state}
                      error={errors.state}
                      onChange={handleChange}
                    />

                    <InputField
                      label={getText('registration.district') || 'District'}
                      name="district"
                      type="text"
                      required
                      placeholder="Enter district"
                      value={formData.district}
                      error={errors.district}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <InputField
                      label={getText('registration.city') || 'City'}
                      name="city"
                      type="text"
                      required
                      placeholder="Enter city"
                      value={formData.city}
                      error={errors.city}
                      onChange={handleChange}
                    />

                    <InputField
                      label={getText('registration.pincode') || 'Pincode'}
                      name="pincode"
                      type="text"
                      required
                      placeholder="Enter 6-digit pincode"
                      maxLength={6}
                      value={formData.pincode}
                      error={errors.pincode}
                      onChange={handleChange}
                    />

                    <InputField
                      label={getText('registration.website') || 'Website'}
                      name="website"
                      type="url"
                      placeholder="Enter website URL (e.g., https://example.com) (optional)"
                      value={formData.website}
                      error={errors.website}
                      onChange={handleChange}
                    />
                  </div>

                  <InputField
                    label={getText('registration.gst') || 'GST Number'}
                    name="gst"
                    type="text"
                    placeholder="Enter 15-digit GST number (optional)"
                    maxLength={15}
                    value={formData.gst}
                    error={errors.gst}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Membership Info */}
              <div>
                <div className="mb-6 pb-4 border-b-2 border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary-600" />
                    {getText('registration.membershipInfo') || 'Membership Information'}
                  </h2>
                </div>
                <div className="space-y-6 mt-6">
                  <InputField
                    label={
                      getText('registration.natureOfBusiness') ||
                      'Nature of Business'
                    }
                    name="natureOfBusiness"
                    type="textarea"
                    required
                    placeholder="e.g. Agri Business, Auto & Ancillary, Chemical, Construction, Consultancy, etc."
                    rows={3}
                    value={formData.natureOfBusiness}
                    error={errors.natureOfBusiness}
                    onChange={handleChange}
                  />

                  <InputField
                    label={getText('registration.industry') || 'Industry'}
                    name="industry"
                    type="text"
                    required
                    placeholder={
                      getText('registration.industryPlaceholder') ||
                      'Enter industry'
                    }
                    value={formData.industry}
                    error={errors.industry}
                    onChange={handleChange}
                  />

                  {/* Worker Count Input */}
                  <InputField
                    label={getText('registration.workerCount') || 'Number of Workers'}
                    name="workerCount"
                    type="number"
                    required
                    placeholder="Enter number of workers"
                    min="1"
                    value={formData.workerCount}
                    error={errors.workerCount}
                    onChange={handleChange}
                    icon={User}
                  />

                  {/* Fee Calculation Display */}
                  {calculatingFee && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Calculating membership fee...</span>
                      </div>
                    </div>
                  )}

                  {feeCalculation && !calculatingFee && (
                    <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-primary-900">Membership Fee:</span>
                          <span className="text-lg font-bold text-primary-700">
                            ₹{Number(feeCalculation.fee).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className="text-xs text-primary-700">
                          <span className="font-medium">Category:</span> {feeCalculation.pricingCategory}
                        </div>
                        {feeCalculation.breakdown && (
                          <div className="text-xs text-primary-600 mt-2 pt-2 border-t border-primary-200 space-y-1.5">
                            {feeCalculation.breakdown.additionalWorkers > 0 ? (
                              <>
                                <div className="flex justify-between">
                                  <span>Base Fee ({feeCalculation.breakdown.baseWorkers} members):</span>
                                  <span className="font-semibold">₹{Number(feeCalculation.breakdown.baseFee).toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Additional ({feeCalculation.breakdown.additionalWorkers} members):</span>
                                  <span className="font-semibold">₹{Number(feeCalculation.breakdown.additionalFeeAmount).toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between pt-1.5 border-t border-primary-300 font-bold text-primary-800">
                                  <span>Total:</span>
                                  <span>₹{Number(feeCalculation.fee).toLocaleString('en-IN')}</span>
                                </div>
                              </>
                            ) : (
                              <div className="flex justify-between">
                                <span>Fixed Fee ({feeCalculation.breakdown.totalWorkers} {feeCalculation.breakdown.totalWorkers === 1 ? 'member' : 'members'}):</span>
                                <span className="font-semibold">₹{Number(feeCalculation.breakdown.baseFee).toLocaleString('en-IN')}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Unified Plan Info */}
                  {unifiedPlan && !errors.workerCount && (
                    <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="space-y-1">
                        <h4 className="font-semibold text-green-900">
                          {unifiedPlan.name}
                        </h4>
                        <p className="text-sm text-green-700">
                          <span className="font-medium">Duration:</span>{' '}
                          {unifiedPlan.duration || 12}{' '}
                          {(unifiedPlan.durationUnit || 'Months').toLowerCase()}
                        </p>
                        {unifiedPlan.trainingProgram && unifiedPlan.trainingProgram.enabled && (
                          <p className="text-sm text-green-700">
                            <span className="font-medium">Training Program:</span> {unifiedPlan.trainingProgram.duration}-week program included
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={
                    submitting || paymentStatus === 'redirecting' || isLoadingPlans
                  }
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:text-black rounded-lg hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {getText('registration.processing') || 'Processing...'}
                    </>
                  ) : paymentStatus === 'redirecting' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {getText('registration.redirecting') || 'Redirecting...'}
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      {getText('registration.submitButton') ||
                        'Proceed to Payment'}
                    </>
                  )}
                </button>

                <div className="mt-6 text-center space-y-3">
                  <p className="text-sm text-gray-600">
                    {getText('registration.termsPrefix') ||
                      'By submitting this form, you agree to our'}{' '}
                    <Link
                      to="/terms-conditions"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      {getText('footer.termsConditions') || 'Terms & Conditions'}
                    </Link>
                    ,{' '}
                    <Link
                      to="/privacy-policy"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      {getText('footer.privacyPolicy') || 'Privacy Policy'}
                    </Link>{' '}
                    {getText('registration.termsAnd') || 'and'}{' '}
                    <Link
                      to="/refund-policy"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      {getText('footer.refundPolicy') || 'Refund Policy'}
                    </Link>
                    .
                  </p>
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium">
                        {getText('registration.securePayment') || 'Secure Payment'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium">
                        {getText('registration.dataProtected') || 'Data Protected'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium">
                        {getText('registration.instantConfirmation') || 'Instant Confirmation'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

    </>
  );
};

export default RegisterPage;
